import { forIn, isEmpty } from 'lodash';
import { unifyAssetFileContents } from '@/modules/ai-page/unifyAssetFileContents';
import { getStore } from '@/store/zeroDepStore';
import { getArtifactCrossOrigin } from './aiArtifactCredentials';
const isRemoteEntryUrl = (value) => {
    const v = value.trim();
    if (/^https?:\/\//i.test(v))
        return true;
    if (/^\/\/[\w@.~-]/.test(v))
        return true;
    return /^\/[\w@.~-]/.test(v);
};
/** remote runtime 入口：优先 COS URL，否则用 artifact.js（URL 或内联源码转 blob）。 */
export const resolveRemoteRuntimeEntry = (aiProjectInfo) => {
    if (aiProjectInfo?.artifactCos?.js) {
        return { src: aiProjectInfo.artifactCos.js, fromBlob: false };
    }
    const js = aiProjectInfo?.artifact?.js;
    if (!js)
        return null;
    if (isRemoteEntryUrl(js)) {
        return { src: js, fromBlob: false };
    }
    return {
        src: URL.createObjectURL(new Blob([js], { type: 'text/javascript' })),
        fromBlob: true,
    };
};
const AI_PROJECT_INFO_BASE_EXCLUDE_FIELDS = ['ctime', 'mtime', '_ctime', '_mtime'];
export const createAiProjectInfoExclude = ({ excludeArtifact = false } = {}) => [
    ...(excludeArtifact ? ['artifact'] : []),
    ...AI_PROJECT_INFO_BASE_EXCLUDE_FIELDS,
].join(',');
const isAiDeployCosEnabled = (aiDeploySetting) => {
    if (!aiDeploySetting)
        return false;
    if (aiDeploySetting.cosEnable !== true)
        return false;
    return !!aiDeploySetting.cosId;
};
const getProjectAiDeploySetting = () => {
    try {
        return getStore()?.state?.project?.settings?.aiDeploySetting || null;
    }
    catch {
        return null;
    }
};
export const getAiArtifactCosEnabled = () => {
    if (window.GLOBAL_INFO?.configuredAiProjectUploader)
        return true;
    return isAiDeployCosEnabled(getProjectAiDeploySetting());
};
/** 分包产物必须由全局 aiProjectUploader 托管，未配置时不能开启。 */
export const canEnableCodeSplit = () => !!window.GLOBAL_INFO?.configuredAiProjectUploader;
/** 开启云端编辑器时，仅在分包产物可被托管的情况下自动开启按页分包。 */
export const shouldAutoEnableCodeSplit = (useRemoteEditor) => (useRemoteEditor && canEnableCodeSplit());
const initialUrlParams = new URLSearchParams(location.search);
/**
 * crossorigin === false 时不注入 preload（HTML 与下方动态 link 一致），编辑器内预览模式也不注入
 * use-credentials 时，入口预加载、样式和模块导入统一携带凭据。
 */
const shouldInjectAiArtifactPreload = () => window.GLOBAL_INFO?.aiProjectArtifactConfig?.crossorigin !== false && !initialUrlParams.has('wujiAiPageUK') && !initialUrlParams.has('wsUrl');
// 是否启用 slim 模式（仅请求 artifactCos 元信息，artifact 由 COS 还原）。
// 配置了 aiProjectUploader 即开启，dev/branch 也走 slim：服务端已统一在 dev/branch 上传 artifactCos。
// 入参 env/branchId 仅为兼容历史调用方保留，新逻辑不再依赖。
export const shouldUseSlimAiProjectInfo = (_options = {}) => getAiArtifactCosEnabled();
// JS 是进入 COS 路径的最小必要条件，CSS 允许为空字符串或缺省。
export const hasCompleteArtifactCos = (aiProjectInfo) => !!aiProjectInfo?.artifactCos?.js;
const isEsmArtifactCos = (artifactCos) => artifactCos?.meta?.module === 'esm';
const isSameHref = (node, href) => {
    const link = node;
    return link.href === href || link.getAttribute('href') === href;
};
const hasExistingPreload = (href, as) => Array
    .from(document.head.querySelectorAll(`link[rel="preload"][as="${as}"]`))
    .some(node => isSameHref(node, href));
const hasExistingModulePreload = (href) => Array
    .from(document.head.querySelectorAll('link[rel="modulepreload"]'))
    .some(node => isSameHref(node, href));
const appendPreload = (href, as) => {
    if (typeof document === 'undefined' || !href || hasExistingPreload(href, as))
        return;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.setAttribute('as', as);
    link.href = href;
    link.crossOrigin = getArtifactCrossOrigin();
    document.head.appendChild(link);
};
const appendModulePreload = (href) => {
    if (typeof document === 'undefined' || !href || hasExistingModulePreload(href))
        return;
    const link = document.createElement('link');
    link.rel = 'modulepreload';
    link.href = href;
    link.crossOrigin = getArtifactCrossOrigin();
    document.head.appendChild(link);
};
/**
 * 只预加载马上会消费的入口资源，且 `as` / `rel` 必须和实际加载方式一致：
 * - ESM remote runtime：js 走 `import()`（modulepreload），css 走 stylesheet
 * - 经典 runtime：js/css 都走 fetch，凭据策略与预加载一致
 * 页面 chunk（assetFiles）由 defineAsyncComponent 按需 import，预加载会触发 Chrome unused preload 警告
 */
export const preloadArtifactCosAssets = (artifactCos) => {
    if (!artifactCos || !shouldInjectAiArtifactPreload())
        return;
    if (isEsmArtifactCos(artifactCos)) {
        const entryCss = getDynamicEntryCssPaths(artifactCos, false);
        if (entryCss !== null) {
            entryCss.forEach(path => appendPreload(artifactCos.assetFiles[path].url, 'style'));
        }
        else if (artifactCos.css) {
            appendPreload(artifactCos.css, 'style');
        }
        if (artifactCos.js)
            appendModulePreload(artifactCos.js);
        return;
    }
    if (artifactCos.css)
        appendPreload(artifactCos.css, 'fetch');
    if (artifactCos.js)
        appendPreload(artifactCos.js, 'fetch');
};
const ARTIFACT_COS_STYLESHEET_ATTR = 'data-wuji-gogo-artifact-cos-css';
function isCssAssetPath(path) {
    return /\.css($|\?)/i.test(path.split('?')[0]);
}
/** 只有完整、可消费的清单才接管样式加载；历史或未知协议保持全量加载。 */
function getDynamicEntryCssPaths(artifact, allowInline) {
    const meta = artifact?.meta;
    if (meta?.cssLoading !== 'dynamic-import' || !Array.isArray(meta.entryCss))
        return null;
    const paths = [];
    for (const path of meta.entryCss) {
        if (typeof path !== 'string' || !path.startsWith('/dist/') || !/\.css$/i.test(path)
            || /[\\?#]/.test(path) || path.includes('\0')) {
            return null;
        }
        const segments = path.slice('/dist/'.length).split('/');
        if (segments.some(part => !part || part === '.' || part === '..'))
            return null;
        const asset = artifact?.assetFiles?.[path];
        const hasUrl = typeof asset?.url === 'string' && !!asset.url.trim();
        const hasContents = allowInline && (typeof asset?.contents === 'string'
            || (typeof asset?.contentGzipBase64 === 'string' && !!asset.contentGzipBase64.trim()));
        if (!hasUrl && !hasContents)
            return null;
        paths.push(path);
    }
    return [...new Set(paths)];
}
function hasExistingStylesheet(href) {
    return Array
        .from(document.head.querySelectorAll('link[rel="stylesheet"]'))
        .some((node) => {
        const link = node;
        return link.href === href || link.getAttribute('href') === href;
    });
}
function appendStylesheet(href) {
    if (typeof document === 'undefined' || !href || hasExistingStylesheet(href)) {
        return Promise.resolve(null);
    }
    return new Promise((resolve, reject) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.type = 'text/css';
        link.href = href;
        link.setAttribute(ARTIFACT_COS_STYLESHEET_ATTR, '1');
        if (shouldInjectAiArtifactPreload()) {
            link.crossOrigin = getArtifactCrossOrigin();
        }
        link.onload = () => resolve(link);
        link.onerror = () => reject(new Error(`Failed to load stylesheet: ${href}`));
        document.head.appendChild(link);
    });
}
export function collectArtifactCosStylesheetUrls(artifactCos) {
    if (!artifactCos)
        return [];
    const entryCss = getDynamicEntryCssPaths(artifactCos, false);
    if (entryCss !== null) {
        return [...new Set(entryCss.map(path => artifactCos.assetFiles[path].url))];
    }
    const urls = [];
    if (artifactCos.css) {
        urls.push(artifactCos.css);
    }
    // 页面 chunk 抽出的 scoped css（esbuild 不会随 JS import 自动挂）
    if (!isEmpty(artifactCos.assetFiles)) {
        forIn(artifactCos.assetFiles, (asset, path) => {
            if (asset.url && isCssAssetPath(path)) {
                urls.push(asset.url);
            }
        });
    }
    return [...new Set(urls)];
}
async function loadStylesheetUrls(urls) {
    const results = await Promise.allSettled(urls.map(url => appendStylesheet(url)));
    results.forEach((result, index) => {
        if (result.status === 'rejected') {
            console.error('[xy-client] load artifactCos stylesheet failed', urls[index], result.reason);
        }
    });
}
/** 等待宿主负责的样式；新产物的异步样式由 import() 自行加载。 */
export async function loadArtifactCosStylesheets(artifactCos) {
    if (!artifactCos)
        return;
    const urls = collectArtifactCosStylesheetUrls(artifactCos);
    if (urls.length) {
        await loadStylesheetUrls(urls);
    }
}
export function disposeArtifactCosStylesheets() {
    if (typeof document === 'undefined')
        return;
    document.head.querySelectorAll(`link[${ARTIFACT_COS_STYLESHEET_ATTR}]`).forEach(el => el.remove());
}
function pushStylesheetHref(css, hrefs, blobHrefs) {
    if (isRemoteEntryUrl(css)) {
        hrefs.push(css);
        return;
    }
    const href = URL.createObjectURL(new Blob([css], { type: 'text/css' }));
    hrefs.push(href);
    blobHrefs.push(href);
}
export function collectArtifactStylesheetHrefs(artifact) {
    const hrefs = [];
    const blobHrefs = [];
    if (!artifact)
        return { hrefs, blobHrefs };
    const entryCss = getDynamicEntryCssPaths(artifact, true);
    if (entryCss !== null) {
        entryCss.forEach((path) => {
            const asset = artifact.assetFiles[path];
            if (typeof asset.url === 'string' && asset.url.trim())
                hrefs.push(asset.url);
            else if (typeof asset.contents === 'string')
                pushStylesheetHref(asset.contents, hrefs, blobHrefs);
        });
        return { hrefs: [...new Set(hrefs)], blobHrefs };
    }
    if (artifact.css)
        pushStylesheetHref(artifact.css, hrefs, blobHrefs);
    if (!isEmpty(artifact.assetFiles)) {
        forIn(artifact.assetFiles, (asset, path) => {
            if (!isCssAssetPath(path))
                return;
            if (asset.url)
                hrefs.push(asset.url);
            else if (typeof asset.contents === 'string' && asset.contents) {
                pushStylesheetHref(asset.contents, hrefs, blobHrefs);
            }
        });
    }
    return { hrefs: [...new Set(hrefs)], blobHrefs };
}
export async function loadArtifactStylesheets(artifact) {
    const entryCss = getDynamicEntryCssPaths(artifact, true);
    if (entryCss !== null) {
        // 先解压，再按清单顺序插入 link，避免混合 URL / gzip 样式因完成顺序改变 CSS 层叠。
        const entries = await Promise.all(entryCss.map(async (path) => {
            const asset = artifact.assetFiles[path];
            if ((typeof asset.url === 'string' && asset.url.trim()) || typeof asset.contents === 'string')
                return [path, asset];
            const bytes = await unifyAssetFileContents(asset);
            return [path, { ...asset, contents: bytes ? new TextDecoder().decode(bytes) : '' }];
        }));
        const { hrefs, blobHrefs } = collectArtifactStylesheetHrefs({
            ...artifact,
            assetFiles: Object.fromEntries(entries),
        });
        await loadStylesheetUrls(hrefs);
        return blobHrefs;
    }
    const { hrefs, blobHrefs } = collectArtifactStylesheetHrefs(artifact);
    if (artifact?.assetFiles) {
        await Promise.all(Object.entries(artifact.assetFiles).map(async ([path, asset]) => {
            if (!isCssAssetPath(path) || asset.url || asset.contents)
                return;
            const bytes = await unifyAssetFileContents(asset);
            if (!bytes?.length)
                return;
            pushStylesheetHref(new TextDecoder().decode(bytes), hrefs, blobHrefs);
        }));
    }
    if (hrefs.length) {
        await loadStylesheetUrls(hrefs);
    }
    return blobHrefs;
}
const fetchAssetText = async (url) => {
    const response = await fetch(url, {
        credentials: getArtifactCrossOrigin() === 'use-credentials' ? 'include' : 'same-origin',
    });
    if (!response.ok) {
        throw new Error(`Failed to fetch AI artifact asset: ${url}`);
    }
    return await response.text();
};
export const resolveArtifactCosToArtifact = async (artifactCos) => {
    if (!artifactCos?.js) {
        throw new Error('artifactCos.js is required');
    }
    preloadArtifactCosAssets(artifactCos);
    const [js, css] = await Promise.all([
        fetchAssetText(artifactCos.js),
        artifactCos.css ? fetchAssetText(artifactCos.css) : Promise.resolve(artifactCos?.css ?? ''),
    ]);
    return {
        ...artifactCos,
        js,
        css,
    };
};
